import React from 'react';
import { Link } from 'react-router-dom';

// The small slice of markdown help articles and chatbot answers use:
// paragraphs, numbered and bulleted lists, **bold**, `code`, [links](/path)
// and {{shot:N}} (screenshot N of the article, placed inline). Rendered as
// React elements — never as HTML — so nothing in an article or an answer
// can inject markup.

function inline(text, keyBase, onNavigate) {
  const out = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith('**')) {
      out.push(<strong key={key} className="font-semibold text-gray-900">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(<code key={key} className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em]">{tok.slice(1, -1)}</code>);
    } else {
      const [, label, href] = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      out.push(href.startsWith('/')
        ? <Link key={key} to={href} onClick={onNavigate} className="text-brand-700 underline">{label}</Link>
        : <a key={key} href={href} target="_blank" rel="noreferrer" className="text-brand-700 underline">{label}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function HelpMarkdown({ text, renderShot, onNavigate, className = '' }) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null;
  let para = [];

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', text: para.join(' ') });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    const shot = line.match(/^\{\{shot:(\d+)\}\}$/);
    const ol = line.match(/^(\d+)[.)]\s+(.*)$/);
    const ul = line.match(/^[-*•]\s+(.*)$/);
    if (!line) { flushPara(); flushList(); continue; }
    if (shot) { flushPara(); flushList(); blocks.push({ type: 'shot', n: Number(shot[1]) }); continue; }
    if (ol || ul) {
      flushPara();
      const type = ol ? 'ol' : 'ul';
      if (!list || list.type !== type) { flushList(); list = { type, items: [], start: ol ? Number(ol[1]) : 1 }; }
      list.items.push(ol ? ol[2] : ul[1]);
      continue;
    }
    // A line after a list item, not itself a list item, continues it.
    if (list && /^\s{2,}/.test(raw)) { list.items[list.items.length - 1] += ` ${line}`; continue; }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();

  return (
    <div className={`space-y-2.5 text-sm leading-relaxed text-gray-700 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === 'shot') return renderShot ? <React.Fragment key={i}>{renderShot(b.n)}</React.Fragment> : null;
        if (b.type === 'p') return <p key={i}>{inline(b.text, i, onNavigate)}</p>;
        const Tag = b.type;
        return (
          <Tag key={i} start={b.type === 'ol' ? b.start : undefined} className={`${b.type === 'ol' ? 'list-decimal' : 'list-disc'} pl-5 space-y-1.5`}>
            {b.items.map((item, j) => <li key={j}>{inline(item, `${i}-${j}`, onNavigate)}</li>)}
          </Tag>
        );
      })}
    </div>
  );
}
